import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/ops/AppShell';
import {
  Package, ListChecks, AlertTriangle, UserCircle2, Users, ArrowRight, CheckCircle2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

function StatTile({ label, value, sub, icon: Icon, danger, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3.5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 leading-tight">{label}</p>
          <p className={`text-[22px] font-semibold leading-tight mt-1.5 ${danger && value > 0 ? 'text-red-700' : 'text-slate-900'}`}>{value}</p>
          {sub && <p className="text-[11px] text-slate-500 mt-1 leading-snug">{sub}</p>}
        </div>
        {Icon && (
          <div className={`w-7 h-7 rounded-md border grid place-items-center shrink-0 ${danger && value > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
            <Icon size={13} className={danger && value > 0 ? 'text-red-600' : 'text-slate-500'} />
          </div>
        )}
      </div>
    </div>
  );
}

function PanelCard({ title, subtitle, action, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid={testid}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div>
          <h3 className="text-[13px] font-semibold text-slate-900 leading-tight">{title}</h3>
          {subtitle && <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </header>
      <div className="px-1.5 py-1.5">{children}</div>
    </div>
  );
}

const fmtRel = (s) => {
  if (!s) return '—';
  const diff = Date.now() - new Date(s).getTime();
  const h = 36e5, d = 24 * h;
  if (diff < h) return `${Math.max(1, Math.floor(diff / 60_000))}m ago`;
  if (diff < d) return `${Math.floor(diff / h)}h ago`;
  if (diff < 30 * d) return `${Math.floor(diff / d)}d ago`;
  return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—';

export default function OpsProductionDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/ops/login'); return; }
    if (!user.roles?.includes('production') && !user.roles?.includes('admin')) { navigate('/ops/login'); return; }
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ops/dashboards/production`, getAuth());
      setData(data);
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);

  if (authLoading || loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-7 h-7 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  const t = data.tiles;
  const isLead = !!data.is_production_lead;

  return (
    <AppShell pageTitle="Dashboard" subtitle="Production workload" activeNav="dashboard" onNavigate={() => {}} role="production">
      <div className="px-6 py-5 space-y-4" data-testid="production-dashboard-page">

        <div className={`grid grid-cols-1 sm:grid-cols-3 ${isLead ? 'lg:grid-cols-4' : ''} gap-3`} data-testid="production-stat-tiles">
          <StatTile testid="tile-active-jobs"    label="Active Jobs"     value={t.active_jobs.count}    icon={Package} />
          <StatTile testid="tile-my-tasks"       label="My Tasks"        value={t.my_tasks.count}       sub="Open" icon={ListChecks} />
          <StatTile testid="tile-tasks-overdue"  label="Tasks Overdue"   value={t.tasks_overdue.count}  icon={AlertTriangle} danger />
          {isLead && t.awaiting_assignment && (
            <StatTile testid="tile-awaiting-assignment" label="Awaiting Assignment" value={t.awaiting_assignment.count} sub="Jobs with unassigned tasks" icon={UserCircle2} />
          )}
        </div>

        <PanelCard
          title="My tasks"
          subtitle="Sorted by deadline"
          testid="my-tasks-card"
          action={
            <button onClick={() => navigate('/ops/production/jobs')} className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="my-tasks-view-jobs">
              All jobs <ArrowRight size={11} />
            </button>
          }
        >
          {data.my_tasks.length === 0 ? (
            <p className="text-[12px] text-slate-500 px-3 py-6 text-center">No open tasks assigned to you.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.my_tasks.map(task => (
                <li key={task.id} data-testid={`my-task-row-${task.id}`}>
                  <button
                    onClick={() => navigate(`/ops/production/jobs?job=${task.job_id}&task=${task.id}`)}
                    className="w-full flex items-center justify-between gap-3 px-2.5 py-2 hover:bg-slate-50/60 rounded-md text-left"
                  >
                    <div className="min-w-0">
                      <p className="text-[13px] text-slate-900 truncate leading-tight">{task.name}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {task.client_name} — {task.loved_one_first_name}
                      </p>
                    </div>
                    <span className={`ops-pill shrink-0 px-1.5 ${
                      task.is_overdue ? 'bg-red-50 text-red-700 border-red-200' :
                      task.deadline ? 'bg-slate-50 text-slate-700 border-slate-200' :
                      'bg-slate-50 text-slate-400 border-slate-200'
                    }`}>
                      {task.deadline ? (task.is_overdue ? `Overdue · ${fmtDate(task.deadline)}` : fmtDate(task.deadline)) : 'No deadline'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </PanelCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <PanelCard title="Recently completed" subtitle="Last 10 tasks finished" testid="recent-completed-card">
            {data.recently_completed.length === 0 ? (
              <p className="text-[12px] text-slate-500 px-3 py-6 text-center">Nothing finished yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.recently_completed.map(c => (
                  <li key={c.id} className="flex items-center justify-between gap-3 px-2.5 py-2" data-testid={`recent-completed-${c.id}`}>
                    <div className="min-w-0 flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[13px] text-slate-900 truncate leading-tight">{c.name}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{c.client_name}</p>
                      </div>
                    </div>
                    <span className="text-[11px] text-slate-400 shrink-0">{fmtRel(c.completed_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </PanelCard>

          {isLead && data.team_workload && (
            <PanelCard
              title="Team workload"
              subtitle="Open tasks per production user"
              testid="team-workload-card"
            >
              {data.team_workload.length === 0 ? (
                <p className="text-[12px] text-slate-500 px-3 py-6 text-center">No production users.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {data.team_workload.map(w => (
                    <li key={w.user_id} className="flex items-center justify-between gap-3 px-2.5 py-2" data-testid={`workload-row-${w.user_id}`}>
                      <div className="min-w-0 flex items-center gap-2">
                        <Users size={13} className="text-slate-400 shrink-0" />
                        <p className="text-[13px] text-slate-900 truncate leading-tight">{w.name}</p>
                      </div>
                      <span className="ops-pill bg-slate-50 text-slate-700 border-slate-200 px-1.5 shrink-0">
                        {w.open_tasks} open
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </PanelCard>
          )}
        </div>
      </div>
    </AppShell>
  );
}
